import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import { OrcamentosService, CategoriasService, fmtMoeda, mesAtual } from '../services';

const initForm = { categoria_id: '', mes_referencia: mesAtual(), valor_limite: '' };

export default function Orcamentos() {
  const [orcamentos, setOrcamentos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [mes, setMes] = useState(mesAtual());
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(initForm);
  const [saving, setSaving] = useState(false);
  const [erro, setErro] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    OrcamentosService.listar(mes).then(setOrcamentos).finally(() => setLoading(false));
  }, [mes]);

  useEffect(() => {
    load();
    CategoriasService.listar({ tipo: 'despesa' }).then(setCategorias);
  }, [load]);

  const openNew = () => { setEditing(null); setForm({ ...initForm, mes_referencia: mes }); setErro(''); setModal(true); };
  const openEdit = (o) => { setEditing(o); setForm({ categoria_id: o.categoria_id, mes_referencia: o.mes_referencia, valor_limite: o.valor_limite }); setErro(''); setModal(true); };

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true); setErro('');
    try {
      if (editing) await OrcamentosService.atualizar(editing.id, form);
      else await OrcamentosService.criar(form);
      setModal(false); load();
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao salvar');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir este orçamento?')) return;
    try { await OrcamentosService.excluir(id); load(); }
    catch (err) { alert(err.response?.data?.erro || 'Erro'); }
  };

  const totalLimite = orcamentos.reduce((s, o) => s + parseFloat(o.valor_limite), 0);
  const totalGasto = orcamentos.reduce((s, o) => s + parseFloat(o.valor_gasto), 0);

  return (
    <Layout title="Orçamentos">
      <div className="flex-between mb-3">
        <div className="flex-gap">
          <input type="month" value={mes} onChange={e => setMes(e.target.value)}
            style={{ padding: '7px 10px', border: '1.5px solid var(--gray-200)', borderRadius: 7, fontSize: 13 }} />
          <div className="stat-card" style={{ margin: 0, padding: '10px 16px', minWidth: 'unset' }}>
            <span className="stat-label" style={{ fontSize: 10 }}>Gasto / Limite</span>
            <span style={{ fontWeight: 600, fontSize: 14 }}>
              <span style={{ color: totalGasto > totalLimite ? 'var(--danger)' : 'var(--gray-700)' }}>{fmtMoeda(totalGasto)}</span>
              <span style={{ color: 'var(--gray-400)' }}> / {fmtMoeda(totalLimite)}</span>
            </span>
          </div>
        </div>
        <button className="btn btn-primary" onClick={openNew}>+ Novo Orçamento</button>
      </div>

      {loading ? (
        <div className="loading-wrap"><div className="spinner" /></div>
      ) : orcamentos.length === 0 ? (
        <div className="empty-state card">
          <div className="empty-icon">📋</div>
          <p>Nenhum orçamento para este mês.</p>
          <button className="btn btn-primary mt-2" onClick={openNew}>Criar orçamento</button>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
          {orcamentos.map(o => {
            const gasto = parseFloat(o.valor_gasto);
            const limite = parseFloat(o.valor_limite);
            const pct = Math.min(100, (gasto / limite) * 100);
            const excedido = gasto > limite;
            return (
              <div key={o.id} className="card">
                <div className="card-body">
                  <div className="flex-between mb-2">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 36, height: 36, borderRadius: 8, background: o.categoria_cor || '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17 }}>
                        {o.categoria_icone}
                      </div>
                      <span style={{ fontWeight: 600, fontSize: 14 }}>{o.categoria_nome}</span>
                    </div>
                    <div className="flex-gap gap-sm">
                      <button className="btn btn-ghost btn-icon btn-sm" onClick={() => openEdit(o)}>✏️</button>
                      <button className="btn btn-ghost btn-icon btn-sm" style={{ color: 'var(--danger)' }} onClick={() => handleDelete(o.id)}>🗑️</button>
                    </div>
                  </div>

                  <div className="progress-bar-wrap mb-1">
                    <div className="progress-bar-fill"
                      style={{ width: `${pct}%`, background: excedido ? 'var(--danger)' : pct > 80 ? 'var(--warning)' : 'var(--success)' }} />
                  </div>

                  <div className="flex-between">
                    <span style={{ fontSize: 13, fontWeight: 600, color: excedido ? 'var(--danger)' : 'var(--gray-700)' }}>
                      {fmtMoeda(gasto)} gastos
                    </span>
                    <span style={{ fontSize: 12, color: 'var(--gray-400)' }}>
                      limite: {fmtMoeda(limite)}
                    </span>
                  </div>

                  {excedido && (
                    <div style={{ marginTop: 8, padding: '6px 10px', background: 'var(--danger-light)', borderRadius: 6, fontSize: 12, color: 'var(--danger)', fontWeight: 500 }}>
                      ⚠️ Excedido em {fmtMoeda(gasto - limite)}
                    </div>
                  )}
                  {!excedido && (
                    <div style={{ marginTop: 6, fontSize: 12, color: 'var(--gray-400)' }}>
                      Disponível: {fmtMoeda(limite - gasto)} ({(100 - pct).toFixed(0)}%)
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal modal-sm">
            <div className="modal-header">
              <span className="modal-title">{editing ? 'Editar Orçamento' : 'Novo Orçamento'}</span>
              <button className="modal-close" onClick={() => setModal(false)}>×</button>
            </div>
            <form onSubmit={handleSave}>
              <div className="modal-body">
                {erro && <div className="alert alert-danger">{erro}</div>}
                {!editing && (
                  <div className="form-group">
                    <label className="form-label">Categoria *</label>
                    <select className="form-control" required value={form.categoria_id}
                      onChange={e => setForm(p => ({ ...p, categoria_id: e.target.value }))}>
                      <option value="">Selecionar...</option>
                      {categorias.map(c => <option key={c.id} value={c.id}>{c.icone} {c.nome}</option>)}
                    </select>
                  </div>
                )}
                <div className="form-group">
                  <label className="form-label">Mês de referência *</label>
                  <input type="month" className="form-control" required value={form.mes_referencia}
                    onChange={e => setForm(p => ({ ...p, mes_referencia: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Limite (R$) *</label>
                  <input type="number" step="0.01" min="0.01" className="form-control" required value={form.valor_limite}
                    onChange={e => setForm(p => ({ ...p, valor_limite: e.target.value }))} />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-ghost" onClick={() => setModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Salvando...' : 'Salvar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
